/**
 * Which layouts are on the tab strip, and where the selection lands when one
 * stops being.
 *
 * Pure, and here rather than in the components, for the reason
 * `lib/layout-templates.ts` gives: the interesting logic must not live in the
 * one place this app cannot test. Everything below is index arithmetic whose
 * failures are silent — a selection that quietly moves to a *different* layout
 * looks exactly like a selection that stayed put, and the user finds out by
 * editing the wrong desk.
 *
 * ## `hidden` is workspace data, and it is `true` or absent
 *
 * D8 puts transient things outside the workspace, and the screener's own hidden
 * state is one of them. Curation is not that: a layout that un-hid itself on the
 * next load would be no use, and the choice should follow the user to another
 * machine exactly as the layout's name does. The contract therefore states
 * `hidden` as an optional boolean where absent and `false` say the same thing —
 * so `setLayoutHidden` drops the key rather than writing `false`, and nothing
 * here may distinguish the two.
 *
 * ## Indices, not ids, because that is what selection is
 *
 * `layoutIndex` in `App` is an index into `workspace.layouts`, which is what the
 * tab strip and the panel both address. Hiding does not move anything — the
 * array keeps its shape and only the mark changes — but **removing shifts every
 * later index down by one**, so a delete of a layout *before* the one on screen
 * silently retargets the selection. That is the bug these two functions exist
 * for, and it is invisible: the tab strip re-renders, one tab is gone, and the
 * highlight is still on a tab. Just not the same one.
 */

import type { Layout } from "@qufolio/model";

/** Whether this layout is kept off the tab strip. */
export function isHidden(layout: Layout): boolean {
  return layout.hidden === true;
}

/** Indices of the layouts that appear on the tab strip, in order. */
export function shownIndexes(layouts: readonly Layout[]): number[] {
  const out: number[] = [];
  layouts.forEach((layout, index) => {
    if (!isHidden(layout)) out.push(index);
  });
  return out;
}

/** How many are hidden — the number the panel's disclosure line reports. */
export function hiddenCount(layouts: readonly Layout[]): number {
  return layouts.length - shownIndexes(layouts).length;
}

/**
 * Whether this layout's eye is offered.
 *
 * **The last shown one cannot be hidden.** A tab strip with nothing on it leaves
 * no way back to any layout, and the count button that opens this panel would be
 * reporting a layout the user cannot see. Refused in the UI rather than in the
 * reducer, deliberately: only the caller knows which are shown, and a reducer
 * that silently declined would be a write that reports success and does nothing.
 */
export function canHide(layouts: readonly Layout[], index: number): boolean {
  const layout = layouts[index];
  if (!layout || isHidden(layout)) return false;
  return shownIndexes(layouts).length > 1;
}

/**
 * The nearest index that is shown, searching outward from `from`.
 *
 * Forward first, then back — landing on the next layout along reads as moving
 * on, where landing behind reads as being sent backwards for no reason. `except`
 * treats one id as already hidden, which is what lets the caller ask the
 * question *before* the write it is about to make.
 *
 * Falls back to `from`, clamped: every layout hidden should not render nothing.
 * That state is reachable from an older client, and a workspace showing one
 * hidden layout beats a blank screen with no control on it.
 */
export function nearestShown(
  layouts: readonly Layout[],
  from: number,
  except?: string,
): number {
  const shown = (index: number) => {
    const layout = layouts[index];
    return layout !== undefined && !isHidden(layout) && layout.id !== except;
  };
  const start = Math.max(0, Math.min(from, layouts.length - 1));
  if (shown(start)) return start;
  for (let step = 1; step < layouts.length; step += 1) {
    if (shown(start + step)) return start + step;
    if (shown(start - step)) return start - step;
  }
  return start;
}

/**
 * Where the selection goes when `hiddenId` is hidden.
 *
 * Hiding does not reshape the array, so every other selection is untouched. Only
 * hiding the layout you are looking at moves anything, and then it has to: the
 * alternative is a chart region rendering a layout with no tab, which is how a
 * user loses a desk they can still see.
 */
export function selectionAfterHide(
  layouts: readonly Layout[],
  hiddenId: string,
  current: number,
): number {
  const at = layouts.findIndex((layout) => layout.id === hiddenId);
  if (at < 0 || at !== current) return current;
  return nearestShown(layouts, at, hiddenId);
}

/**
 * Where the selection goes when `removedId` is deleted — an index into the
 * layouts that are **left**.
 *
 * Resolved through the surviving layout's *id* rather than by adjusting the
 * number, because the adjustment is exactly what is easy to get wrong: the two
 * cases differ by one and the wrong answer still renders a layout.
 */
export function selectionAfterRemove(
  layouts: readonly Layout[],
  removedId: string,
  current: number,
): number {
  const at = layouts.findIndex((layout) => layout.id === removedId);
  if (at < 0) return current;
  const survivors = layouts.filter((layout) => layout.id !== removedId);
  if (survivors.length === 0) return 0;

  const currentId = layouts[current]?.id;
  if (currentId !== undefined && currentId !== removedId) {
    const moved = survivors.findIndex((layout) => layout.id === currentId);
    return moved >= 0 ? moved : nearestShown(survivors, 0);
  }
  // The layout on screen is the one going. `at` now names whatever took its
  // place, which is the next one along — and if it was last, the one before it.
  return nearestShown(survivors, Math.min(at, survivors.length - 1));
}
